import type DeviceMapEntity from "../../../map-management/map-entities/device-map-entity";

import { doesDeviceVersionMatch, GENERATION_CODE, PRODUCT_LINE_CODE } from "../../versions-code";
import {
    BATTERY_LEVEL,
    GEN4_VOLTAGE_LEVELS,
    SEED_DEVICE_STATS_BATTERY_LEVELS,
    SPROUT_VOLTAGE_LEVELS_BLOCK,
    SPROUT_VOLTAGE_LEVELS_MICRO,
} from "./definitions";

function findLevelFromValue(levels: { max: number; level: BATTERY_LEVEL }[], value: number): BATTERY_LEVEL {
    // Find the level that the value falls into
    const foundLevel = levels.find((level, index, array) => {
        if (index === 0) {
            return value < level.max;
        }
        return value >= array[index - 1].max && value < level.max;
    });
    if (foundLevel) {
        return foundLevel.level;
    }

    // If voltage is higher than the highest max, return FULL
    if (value >= levels[levels.length - 1].max) {
        return BATTERY_LEVEL.FULL;
    }

    // Nothing found, return UNKNOWN
    return BATTERY_LEVEL.UNKNOWN;
}

/**
 * Gets the battery level for a device
 */
export const getBatteryLevel = ({ model }: { model: DeviceMapEntity["model"] }): BATTERY_LEVEL => {
    if (!model?.deviceVersionInfo) {
        return handleNonVersionedDevices(model);
    }

    // Check device type & generation based on version string
    const isGen4Device = doesDeviceVersionMatch(model, { generation: GENERATION_CODE.GEN4 });
    const isSproutBlockDevice = doesDeviceVersionMatch(model, {
        generation: GENERATION_CODE.SPROUT,
        productLine: PRODUCT_LINE_CODE.BLOCK,
    });
    const isSproutMicroDevice = doesDeviceVersionMatch(model, {
        generation: GENERATION_CODE.SPROUT,
        productLine: PRODUCT_LINE_CODE.MICRO,
    });
    const isSeedDevice = doesDeviceVersionMatch(model, { generation: GENERATION_CODE.SEED });
    if (isGen4Device) {
        // lastBatteryVoltageReading may not exist if we haven't received any data yet
        const voltage = model.gen4NetworkInfo?.lastBatteryVoltageReading?.value;
        return findLevelFromValue(GEN4_VOLTAGE_LEVELS, voltage);
    }
    if (isSproutBlockDevice) {
        const voltage = model.sproutNetworkInfo.batteryVoltage;
        return findLevelFromValue(SPROUT_VOLTAGE_LEVELS_BLOCK, voltage);
    }
    if (isSproutMicroDevice) {
        const voltage = model.sproutNetworkInfo.batteryVoltage;
        return findLevelFromValue(SPROUT_VOLTAGE_LEVELS_MICRO, voltage);
    }
    if (isSeedDevice) {
        const batt = model.device_stats?.batt;
        return findLevelFromValue(SEED_DEVICE_STATS_BATTERY_LEVELS, batt);
    }

    // Default in case device somehow doesnt match any version
    return BATTERY_LEVEL.UNKNOWN;
};

/**
 * Handles devices that don't have a deviceVersionInfo such as Sensoterra
 */
export const handleNonVersionedDevices = (model: DeviceMapEntity["model"]): BATTERY_LEVEL => {
    const isSensoterraDevice = model?.category === "SensoterraMoisture";

    if (isSensoterraDevice) {
        const batt = model.device_stats?.batt;
        // Sensoterra devices use the Seed device stats battery levels
        return findLevelFromValue(SEED_DEVICE_STATS_BATTERY_LEVELS, batt);
    }

    return BATTERY_LEVEL.UNKNOWN;
};
